/**
 * Shared constants and utility functions used by the calling-rule,
 * POC-assignment, and POC-reassignment modal forms.
 */

import type { Dispatch, SetStateAction } from "react";
import {
  isTimestampDataType,
  normalizeQueryBuilderOptions,
  type QueryBuilderConfig,
  type QueryBuilderOption,
  type QueryBuilderValue,
} from "../../../data-model/queryBuilder";
import type { CallingRuleAction } from "../../../pages/configurations/pages/callingConfiguration/callingRuleUtils";
import { getFieldDef } from "../../queryBuilder/queryBuilderUtils";
import type { QueryBuilderValueSearchOption } from "../../queryBuilder/useQueryBuilderValueSearch";

export const TRIGGER_TYPE_OPTIONS = [
  { value: "api", label: "api" },
  { value: "cron", label: "cron" },
  { value: "order_start", label: "order_start" },
  { value: "before_stp", label: "before_stp" },
  { value: "before_second_stp", label: "before_second_stp" },
  { value: "after_first_stp", label: "after_first_stp" },
  { value: "after_second_stp", label: "after_second_stp" },
];

/**
 * Returns the exact supported trigger vocabulary.
 *
 * @param _currentType - Existing stored value; unsupported legacy values are intentionally omitted
 * @returns Supported trigger options
 */
export const getTriggerTypeOptions = (_currentType: string) =>
  TRIGGER_TYPE_OPTIONS;

export const BASE_ACTION_TYPE_OPTIONS = [
  { value: "set", label: "Set" },
  { value: "set_random", label: "Set Random" },
  { value: "notify", label: "Notify" },
];

const BASE_ACTION_TYPE_VALUES = new Set(
  BASE_ACTION_TYPE_OPTIONS.map((o) => o.value),
);

/**
 * Returns action-field dropdown options, including the current value when it is
 * not present in the query-builder field list (e.g. legacy Firestore data).
 *
 * @param baseOptions - Field options from the calling condition query builder config
 * @param currentField - Currently selected action field value
 * @returns Options suitable for SearchableSelect / Select components
 */
export const getActionFieldOptions = (
  baseOptions: QueryBuilderOption[],
  currentField: string,
): QueryBuilderOption[] => {
  const options = normalizeQueryBuilderOptions(baseOptions);
  if (!currentField || options.some((o) => o.value === currentField)) {
    return options;
  }
  return normalizeQueryBuilderOptions([
    ...options,
    { value: currentField, label: currentField },
  ]);
};

/** Returns dropdown options with the Firestore-stored value included if it falls outside the predefined list. */
export const getActionTypeOptions = (currentType: string) => {
  if (!currentType || BASE_ACTION_TYPE_VALUES.has(currentType)) {
    return BASE_ACTION_TYPE_OPTIONS;
  }
  return [
    ...BASE_ACTION_TYPE_OPTIONS,
    { value: currentType, label: currentType },
  ];
};

/** Normalises an action value to a string array for chip-based editing. */
export const toValueArray = (value: string | string[]): string[] =>
  Array.isArray(value) ? value : value ? [value] : [];

/** Normalises an action value to a single string for text-based editing. */
export const toValueString = (value: string | string[]): string =>
  Array.isArray(value) ? value.join(", ") : value;

/**
 * Returns whether the selected action field should use a date picker for its value.
 *
 * @param conditionConfig - Query builder config (includes `dataType` per field)
 * @param selectedField - Selected action field column name
 * @returns True when the field is TIMESTAMP, DATE, or DATETIME
 */
export const isActionFieldTimestamp = (
  conditionConfig: QueryBuilderConfig,
  selectedField: string,
): boolean => {
  const fieldDef = getFieldDef(conditionConfig, selectedField);
  return isTimestampDataType(fieldDef?.dataType);
};

/** Safely deep-clones an action value that may be a string or string[]. */
export const cloneActionValue = (
  value: string | string[],
): string | string[] => (Array.isArray(value) ? [...value] : value);

/**
 * Returns single-select value options for a `set` action from the condition query
 * builder config (field `valueOptions` / condition-list top_values).
 *
 * @param conditionConfig - Query builder config from the rule's condition step
 * @param selectedField - Selected action field column name
 * @param currentValue - Current action value (string or legacy array)
 * @returns Initial options for AsyncSearchableSelect, including the current value when missing from top_values
 */
export const getActionValueOptions = (
  conditionConfig: QueryBuilderConfig,
  selectedField: string,
  currentValue: string | string[],
): QueryBuilderOption[] => {
  const fieldDef = getFieldDef(conditionConfig, selectedField);
  const baseOptions = fieldDef?.valueOptions ?? [];
  const currentStr = toValueString(currentValue);
  if (!currentStr || baseOptions.some((o) => o.value === currentStr)) {
    return baseOptions;
  }
  return [...baseOptions, { value: currentStr, label: currentStr }];
};

/**
 * Maps action value options to MultiSelectWithTag `{ id, label }` shape, including
 * any selected values missing from top_values (same as QueryBuilder condition rows).
 *
 * @param conditionConfig - Query builder config from the rule's condition step
 * @param selectedField - Selected action field column name
 * @param currentValue - Current action value (string or string[])
 * @returns Options for MultiSelectWithTag and useQueryBuilderValueSearch
 */
export const getActionValueOptionsAsMultiSelect = (
  conditionConfig: QueryBuilderConfig,
  selectedField: string,
  currentValue: string | string[],
): QueryBuilderValueSearchOption[] => {
  const baseOptions = getActionValueOptions(
    conditionConfig,
    selectedField,
    currentValue,
  );
  const base = baseOptions.map((o) => ({ id: o.value, label: o.label }));
  const selectedValues = toValueArray(currentValue);
  const existingIds = new Set(base.map((o) => o.id));
  const missing = selectedValues
    .filter((v) => !existingIds.has(v))
    .map((v) => ({ id: v, label: v }));
  return [...base, ...missing];
};

/** Rule shape with an actions array for inline list editing. */
export interface RuleWithActions {
  id: string;
  actions: CallingRuleAction[];
}

/** Updates one rule's actions array via a patch function. */
export function patchRuleActions<T extends RuleWithActions>(
  rules: T[],
  ruleId: string,
  patch: (actions: CallingRuleAction[]) => CallingRuleAction[],
): T[] {
  return rules.map((rule) =>
    rule.id === ruleId
      ? { ...rule, actions: patch(rule.actions.map((a) => ({ ...a }))) }
      : rule,
  );
}

/** Updates the field on a single action row. */
export function updateActionField(
  actions: CallingRuleAction[],
  index: number,
  value: string,
): CallingRuleAction[] {
  return actions.map((action, i) =>
    i === index ? { ...action, field: value } : action,
  );
}

/** Updates the type on a single action row and normalizes value shape when needed. */
export function updateActionType(
  actions: CallingRuleAction[],
  index: number,
  newType: string,
): CallingRuleAction[] {
  return actions.map((action, i) => {
    if (i !== index) return action;
    const wasSetRandom = action.type === "set_random";
    const isSetRandom = newType === "set_random";
    let newValue: string | string[] = action.value;
    if (!wasSetRandom && isSetRandom) {
      newValue = toValueArray(action.value);
    } else if (wasSetRandom && !isSetRandom) {
      newValue = toValueString(action.value);
    }
    return { type: newType, field: action.field, value: newValue };
  });
}

/** Updates the string value on a single action row. */
export function updateActionStringValue(
  actions: CallingRuleAction[],
  index: number,
  value: string,
): CallingRuleAction[] {
  return actions.map((action, i) =>
    i === index ? { type: action.type, field: action.field, value } : action,
  );
}

/** Updates the multi-value list on a set_random action row. */
export function updateActionMultiValue(
  actions: CallingRuleAction[],
  index: number,
  values: string[],
): CallingRuleAction[] {
  return actions.map((action, i) =>
    i === index
      ? { type: action.type, field: action.field, value: values }
      : action,
  );
}

/** Appends a blank set action row. */
export function addActionRow(
  actions: CallingRuleAction[],
): CallingRuleAction[] {
  return [...actions, { type: "set", field: "", value: "" }];
}

/** Removes an action row by index. */
export function removeActionRow(
  actions: CallingRuleAction[],
  index: number,
): CallingRuleAction[] {
  return actions.filter((_, i) => i !== index);
}

type InlineEditableRule = RuleWithActions & {
  label: string;
  subtext?: string;
  active: boolean;
  condition: QueryBuilderValue;
};

/**
 * Builds inline edit handlers shared by pharmacy order update rule list hooks.
 *
 * @param setRules - Rule list state setter
 * @param markDirty - Marks the parent form as having unsaved changes
 * @returns Handlers for label, subtext, active, condition, and action edits
 */
export function createRuleListInlineHandlers<T extends InlineEditableRule>(
  setRules: Dispatch<SetStateAction<T[]>>,
  markDirty: () => void,
) {
  const touchRules = (updater: (prev: T[]) => T[]) => {
    markDirty();
    setRules(updater);
  };

  return {
    handleRuleLabelChange: (ruleId: string, label: string) => {
      touchRules((prev) =>
        prev.map((rule) =>
          rule.id === ruleId
            ? { ...rule, label: label.trim() || rule.label }
            : rule,
        ),
      );
    },
    handleRuleSubtextChange: (ruleId: string, subtext: string) => {
      touchRules((prev) =>
        prev.map((rule) => (rule.id === ruleId ? { ...rule, subtext } : rule)),
      );
    },
    handleRuleActiveToggle: (ruleId: string) => {
      touchRules((prev) =>
        prev.map((rule) =>
          rule.id === ruleId ? { ...rule, active: !rule.active } : rule,
        ),
      );
    },
    handleRuleConditionChange: (
      ruleId: string,
      newCondition: QueryBuilderValue,
    ) => {
      touchRules((prev) =>
        prev.map((rule) =>
          rule.id === ruleId ? { ...rule, condition: newCondition } : rule,
        ),
      );
    },
    handleRuleActionFieldChange: (
      ruleId: string,
      index: number,
      value: string,
    ) => {
      touchRules((prev) =>
        patchRuleActions(prev, ruleId, (actions) =>
          updateActionField(actions, index, value),
        ),
      );
    },
    handleRuleActionTypeChange: (
      ruleId: string,
      index: number,
      value: string,
    ) => {
      touchRules((prev) =>
        patchRuleActions(prev, ruleId, (actions) =>
          updateActionType(actions, index, value),
        ),
      );
    },
    handleRuleActionStringValueChange: (
      ruleId: string,
      index: number,
      value: string,
    ) => {
      touchRules((prev) =>
        patchRuleActions(prev, ruleId, (actions) =>
          updateActionStringValue(actions, index, value),
        ),
      );
    },
    handleRuleActionMultiValueChange: (
      ruleId: string,
      index: number,
      values: string[],
    ) => {
      touchRules((prev) =>
        patchRuleActions(prev, ruleId, (actions) =>
          updateActionMultiValue(actions, index, values),
        ),
      );
    },
    handleRuleAddAction: (ruleId: string) => {
      touchRules((prev) =>
        patchRuleActions(prev, ruleId, (actions) => addActionRow(actions)),
      );
    },
    handleRuleRemoveAction: (ruleId: string, index: number) => {
      touchRules((prev) =>
        patchRuleActions(prev, ruleId, (actions) =>
          removeActionRow(actions, index),
        ),
      );
    },
  };
}
